import React, { useState, useEffect, useRef, useContext } from "react";
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import Button from "components/CustomButtons/Button.js";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { makeStyles } from "@mui/styles";
import AlertDialog from "../components/AlertDialog";
import { FirebaseContext, api } from "common";
import { useTranslation } from "react-i18next";
import moment from "moment/min/moment-with-locales";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { CameraAlt as CameraIcon } from "@mui/icons-material";
import {
  Select,
  MenuItem,
  Paper,
  Grid,
  Box,
  OutlinedInput,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { colors } from "../components/Theme/WebTheme";
import { MAIN_COLOR, FONT_FAMILY } from "../common/sharedFunctions";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    padding: "24px",
    width: "100%",
  },
  pageTitle: {
    fontFamily: "Arial, sans-serif",
    fontSize: "32px",
    fontWeight: "600",
    color: "#202224",
    marginBottom: "32px",
  },
  paper: {
    backgroundColor: theme.palette.common.white,
    borderRadius: 24,
    padding: theme.spacing(6),
    width: "100%",
    maxWidth: 800,
    margin: 0,
    position: "relative",
  },
  form: {
    width: "100%",
    // marginTop: "100px",
  },
  imageSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "20px",
    top: theme.spacing(2),
  },
  avatarContainer: {
    width: 120,
    height: 120,
    backgroundColor: "#ECECEE",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(1),
    cursor: "pointer",
    border: "1px solid #E5E7EB",
  },
  uploadText: {
    color: "#000",
    fontSize: "0.875rem",
    fontFamily: FONT_FAMILY,
    marginTop: theme.spacing(1),
  },
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    columnGap: theme.spacing(6),
    rowGap: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  textField: {
    "& .MuiFormLabel-root": {
      fontSize: "14px",
      color: "#21272A",
      transform: "none",
      position: "static",
      marginBottom: "4px",
      fontFamily: "Roboto, sans-serif",
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#F8F8F8",
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
      "&:after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "1px",
        backgroundColor: "#E5E7EB",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px",
      fontFamily: FONT_FAMILY,
      fontSize: "1rem",
      color: "##21272A",
      backgroundColor: "##F8F8F8",
    },
    marginBottom: "14px",
  },
  buttonStyle: {
    marginTop: theme.spacing(6),
    color: colors.WHITE,
    borderRadius: 50,
    padding: "14px 0",
    width: "40%",
    fontFamily: FONT_FAMILY,
    fontWeight: "bold",
    fontSize: "1rem",
    textTransform: "none",
    margin: "0 auto",
    display: "block",
  },
  selectField: {
    "& .MuiFormLabel-root": {
      fontSize: "0.875rem",
      color: "##21272A",
      transform: "none",
      position: "static",
      marginBottom: "8px",
      fontFamily: FONT_FAMILY,
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#F9FAFB",
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
      "&:after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "1px",
        backgroundColor: "#E5E7EB",
      },
    },
    "& .MuiSelect-select": {
      padding: "12px 16px",
      fontFamily: FONT_FAMILY,
      fontSize: "1rem",
      color: "#21272A",
      backgroundColor: "#F9FAFB",
    },
    marginBottom: "24px",
  },
}));

const MyProfile = () => {
  const { authRef, RecaptchaVerifier, unlink, linkWithPhoneNumber } =
    useContext(FirebaseContext);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    updateProfile,
    updateWebProfileImage,
    checkUserExists,
    requestMobileOtp,
    updateAuthMobile,
  } = api;
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const classes = useStyles();
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });
  const languagedata = useSelector((state) => state.languagedata);
  const [langSelection, setLangSelection] = useState(0);
  const [multiLanguage, setMultiLanguage] = useState();
  const fileInputRef = useRef();
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState(null);
  const settings = useSelector((state) => state.settingsdata.settings);

  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
    usertype: "",
    verifyId: "",
  });

  const [otp, setOtp] = useState("");
  const [otpCalled, setOtpCalled] = useState(false);
  const [updateCalled, setUpdateCalled] = useState(false);
  const [confirmCodeFunction, setConfirmCodeFunction] = useState();

  useEffect(() => {
    if (languagedata.langlist) {
      let arr = Object.keys(languagedata.langlist);
      for (let i = 0; i < arr.length; i++) {
        if (
          auth.profile &&
          auth.profile.lang &&
          auth.profile.lang.langLocale &&
          auth.profile.lang.langLocale ===
            languagedata.langlist[arr[i]].langLocale
        ) {
          setLangSelection(i);
        }
      }
      setMultiLanguage(languagedata.langlist);
      setRole(auth.profile.usertype);
    }
  }, [languagedata.langlist, auth.profile, setMultiLanguage, setLangSelection]);

  useEffect(() => {
    if (auth.profile && auth.profile.uid) {
      setData({
        firstName: auth.profile.firstName,
        lastName: auth.profile.lastName,
        mobile: auth.profile.mobile,
        usertype: auth.profile.usertype,
        profile_image: auth.profile.profile_image
          ? auth.profile.profile_image
          : "",
        verifyId: auth.profile.verifyId ? auth.profile.verifyId : "",
      });
      if (updateCalled) {
        setLoading(false);
        setCommonAlert({ open: true, msg: t("profile_updated") });
        setUpdateCalled(false);
      }
    }
  }, [auth.profile, t, updateCalled]);

  const updateData = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const completeSubmit = () => {
    setUpdateCalled(true);
    let arr = Object.keys(languagedata.langlist);
    localStorage.setItem(
      "lang",
      JSON.stringify({
        langLocale: multiLanguage[arr[langSelection]].langLocale,
        dateLocale: multiLanguage[arr[langSelection]].dateLocale,
      }),
    );

    dispatch(
      updateProfile({
        firstName: data.firstName,
        lastName: data.lastName,
        mobile: data.mobile,
        verifyId: data.verifyId,
        lang: {
          langLocale: multiLanguage[arr[langSelection]].langLocale,
          dateLocale: multiLanguage[arr[langSelection]].dateLocale,
        },
      }),
    );

    if (selectedImage && role !== "driver") {
      updateWebProfileImage(selectedImage);
    }

    const lang = multiLanguage[arr[langSelection]];
    i18n.addResourceBundle(
      multiLanguage[arr[langSelection]].langLocale,
      "translations",
      multiLanguage[arr[langSelection]].keyValuePairs,
    );
    i18n.changeLanguage(lang.langLocale);
    moment.locale(lang.dateLocale);
    setSelectedImage(null);
    setCommonAlert({ open: true, msg: t("profile_updated") });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      data.firstName &&
      data.firstName.length > 0 &&
      data.lastName &&
      data.lastName.length > 0
    ) {
      if (data.mobile !== auth.profile.mobile) {
        if (data.mobile && data.mobile.length > 6) {
          setLoading(true);
          checkUserExists({ mobile: data.mobile }).then(async (res) => {
            if (res.users && res.users.length > 0) {
              setLoading(false);
              alert(t("user_exists"));
            } else if (res.error) {
              setLoading(false);
              alert(t("email_or_mobile_issue"));
            } else {
              setLoading(false);
              setOtpCalled(true);

              if (settings.customMobileOTP) {
                dispatch(requestMobileOtp(data.mobile));
              } else {
                window.recaptchaVerifier = await new RecaptchaVerifier(
                  authRef(),
                  "sign-in-button",
                  {
                    size: "invisible",
                  },
                );

                if (auth.profile.mobile && auth.profile.mobile.length > 6) {
                  await unlink(authRef().currentUser, "phone");
                }

                const linkObj = await linkWithPhoneNumber(
                  authRef().currentUser,
                  data.mobile,
                  window.recaptchaVerifier,
                );
                setConfirmCodeFunction(linkObj);
              }
            }
          });
        } else {
          setCommonAlert({ open: true, msg: t("mobile_no_blank_error") });
        }
      } else {
        setLoading(true);
        completeSubmit();
      }
    } else {
      setCommonAlert({ open: true, msg: t("no_details_error") });
    }
  };

  const updateOtp = (e) => {
    setOtp(e.target.value);
  };

  const handleVerify = async () => {
    if (otp && otp.length === 6 && !isNaN(otp)) {
      setLoading(true);
      if (settings.customMobileOTP) {
        const res = await updateAuthMobile(data.mobile, otp);
        if (res.success) {
          completeSubmit();
        } else {
          setOtp("");
          setLoading(false);
          if (res.error === "Error updating user") {
            setCommonAlert({ open: true, msg: t("user_exists") });
          } else {
            setCommonAlert({ open: true, msg: t("otp_validate_error") });
          }
        }
      } else {
        if (confirmCodeFunction) {
          confirmCodeFunction
            .confirm(otp)
            .then((user) => {
              completeSubmit();
            })
            .catch((error) => {
              setOtp("");
              setLoading(false);
              window.recaptchaVerifier.clear();
              setCommonAlert({ open: true, msg: t("otp_validate_error") });
            });
        }
      }
    } else {
      setOtp("");
      setCommonAlert({ open: true, msg: t("otp_validate_error") });
    }
    setOtpCalled(false);
    window.recaptchaVerifier = null;
  };

  const handleClose = () => {
    setOtp("");
    setOtpCalled(false);
    window.recaptchaVerifier = null;
  };

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: "" });
  };

  const handleLanguageSelect = (event) => {
    setLangSelection(event.target.value);
  };

  const profileImageChange = async (e) => {
    setSelectedImage(e.target.files[0]);
  };

  return auth.loading ? (
    <CircularLoading />
  ) : (
    <Container
      component="main"
      className={classes.container}
      disableGutters
      maxWidth={false}
    >
      <CssBaseline />
      <div id="sign-in-button" />

      <Box className={classes.mainContainer}>
        <Typography variant="h1" className={classes.pageTitle}>
          {t("profile_title")}
        </Typography>

        <Paper className={classes.paper} elevation={0}>
          <div className={classes.imageSection}>
            <div
              className={classes.avatarContainer}
              onClick={() => role !== "driver" && fileInputRef.current.click()}
              style={{ cursor: role === "driver" ? "default" : "pointer" }}
            >
              {selectedImage ? (
                <img
                  src={URL.createObjectURL(selectedImage)}
                  alt="Profile"
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              ) : data.profile_image ? (
                <img
                  src={data.profile_image}
                  alt="Profile"
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <CameraIcon
                  style={{
                    width: 32,
                    height: 32,
                    color: "#9CA3AF",
                    visibility: role === "driver" ? "hidden" : "visible",
                  }}
                />
              )}
            </div>
            {role !== "driver" && (
              <Typography className={classes.uploadText}>
                {t("upload_image")}
              </Typography>
            )}
            <input
              type="file"
              hidden
              ref={fileInputRef}
              onChange={profileImageChange}
              accept="image/*"
              disabled={role === "driver"}
            />
          </div>

          <form
            onSubmit={handleSubmit}
            className={classes.form}
            style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
          >
            <div className={classes.gridContainer}>
              <TextField
                className={
                  isRTL === "rtl" ? classes.rootRtl : classes.textField
                }
                variant="outlined"
                required
                fullWidth
                label={t("firstname")}
                name="firstName"
                value={data.firstName}
                onChange={updateData}
                disabled={role === "driver"}
              />

              <TextField
                className={
                  isRTL === "rtl" ? classes.rootRtl : classes.textField
                }
                variant="outlined"
                required
                fullWidth
                label={t("lastname")}
                name="lastName"
                value={data.lastName}
                onChange={updateData}
                disabled={role === "driver"}
              />

              <TextField
                className={
                  isRTL === "rtl" ? classes.rootRtl : classes.textField
                }
                variant="outlined"
                required
                fullWidth
                label={t("phone")}
                name="mobile"
                value={data.mobile}
                disabled={true} // Phone is read-only (primary identifier)
                helperText={
                  t("phone_cannot_be_changed") ||
                  "Phone number cannot be changed"
                }
                dir="ltr"
              />

              <TextField
                className={
                  isRTL === "rtl" ? classes.rootRtl : classes.textField
                }
                variant="outlined"
                fullWidth
                label={t("usertype")}
                name="usertype"
                value={data.usertype}
                disabled
              />

              {multiLanguage && multiLanguage.length > 1 && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    component="label"
                    style={{
                      fontSize: "0.875rem",
                      color: "##21272A",
                      marginBottom: "8px",
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    {t(`preferred_language`)}
                  </Typography>
                  <Select
                    className={classes.selectField}
                    value={langSelection}
                    variant="outlined"
                    fullWidth
                    onChange={handleLanguageSelect}
                    displayEmpty
                    labelId="language-label"
                    input={<OutlinedInput />}
                  >
                    {Object.keys(multiLanguage).map((key, index) => (
                      <MenuItem key={key} value={index}>
                        {multiLanguage[key].langName}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              )}
            </div>

            {loading ? (
              <Grid container justifyContent="center" style={{ marginTop: 20 }}>
                <CircularProgress />
              </Grid>
            ) : (
              <Button
                type="submit"
                fullWidth
                variant="contained"
                className={classes.buttonStyle}
                sx={{
                  backgroundColor:
                    (role === "customer"
                      ? "#5BAA59"
                      : role === "driver"
                        ? "#4F913C"
                        : "#DA4CBF") + "!important",
                  "&:hover": {
                    backgroundColor:
                      (role === "customer"
                        ? "#5BAA59"
                        : role === "driver"
                          ? "#4F913C"
                          : "#DA4CBF") + "!important",
                  },
                }}
              >
                {t("save")}
              </Button>
            )}
          </form>
        </Paper>
      </Box>

      <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>
        {commonAlert.msg}
      </AlertDialog>

      <Dialog open={otpCalled} onClose={handleClose}>
        <DialogTitle>{t("otp_here")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label={t("otp")}
            type="numeric"
            fullWidth
            variant="standard"
            onChange={updateOtp}
            value={otp}
          />
        </DialogContent>
        {loading ? (
          <DialogActions style={{ justifyContent: "center" }}>
            <CircularProgress />
          </DialogActions>
        ) : (
          <DialogActions>
            <Button onClick={handleClose}>{t("cancel")}</Button>
            <Button onClick={handleVerify}>{t("verify_otp")}</Button>
          </DialogActions>
        )}
      </Dialog>
    </Container>
  );
};

export default MyProfile;
